import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '@/components/common';
import axios from 'axios';

interface InsurerEntry {
  id: string;
  name: string;
  short_name: string;
  logo_url: string;
  description: string;
  public_profile_slug: string;
  category: string;
  claim_settlement_ratio: string | null;
  customer_rating: string | null;
  products_count: number;
  /** PUBLIC SITE-1: a short preview of what this insurer sells. Capped
   *  server-side (PublicInsurerSerializer.PRODUCT_PREVIEW_LIMIT) — the profile
   *  page serves the full catalogue. */
  products?: { id: string; name: string; category_display?: string }[];
}

export function InsurersDirectory() {
  const [insurers, setInsurers] = useState<InsurerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadInsurers = async () => {
      try {
        const response = await axios.get('/policy/api/public/insurers/');
        setInsurers(response.data.results || response.data);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };

    loadInsurers();
  }, []);

  const filteredInsurers = insurers.filter((insurer) =>
    insurer.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Insurance Companies</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Browse insurance companies and their products
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search insurance companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full max-w-md rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : filteredInsurers.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {searchQuery ? 'No companies match your search.' : 'No insurance companies available.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInsurers.map((insurer) => (
              <Link
                key={insurer.id}
                to={`/insurer/${insurer.public_profile_slug}`}
                className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow p-6 flex flex-col"
              >
                <div className="flex items-center gap-4 mb-3">
                  {insurer.logo_url ? (
                    <img
                      src={insurer.logo_url}
                      alt={insurer.name}
                      className="h-12 w-12 rounded-lg object-contain bg-gray-100 dark:bg-gray-700 p-1"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {insurer.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{insurer.name}</h3>
                    {insurer.short_name && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{insurer.short_name}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-200 capitalize">
                    {insurer.category}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {insurer.products_count} products
                  </span>
                </div>

                {/* PUBLIC SITE-1 asked for "respective product details" — the
                    card previously said only "7 products", which tells a
                    visitor nothing about what is actually sold. */}
                {insurer.products && insurer.products.length > 0 && (
                  <ul className="mb-3 space-y-1">
                    {insurer.products.map((product) => (
                      <li
                        key={product.id}
                        className="truncate text-sm text-gray-700 dark:text-gray-300"
                        title={product.name}
                      >
                        <span aria-hidden="true" className="mr-1.5 text-gray-400">·</span>
                        {product.name}
                        {product.category_display && (
                          <span className="ml-1 text-xs text-gray-400">
                            ({product.category_display})
                          </span>
                        )}
                      </li>
                    ))}
                    {insurer.products_count > insurer.products.length && (
                      <li className="text-xs text-gray-400">
                        +{insurer.products_count - insurer.products.length} more
                      </li>
                    )}
                  </ul>
                )}

                <div className="mt-auto flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  {insurer.customer_rating && (
                    <span>Rating: {insurer.customer_rating}/5</span>
                  )}
                  {insurer.claim_settlement_ratio && (
                    <span>CSR: {insurer.claim_settlement_ratio}%</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Powered by base.Policy.online
          </p>
        </div>
      </footer>
    </div>
  );
}
